import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, CalendarDays, Menu, X } from "lucide-react";

const R = "/gb/en";

const NAV = [
  { label: "Home", to: `${R}/home.html` },
  {
    label: "About us",
    to: `${R}/about-us.html`,
    children: [
      { label: "About us", to: `${R}/about-us.html` },
      { label: "Our Team", to: `${R}/our-team.html` },
    ],
  },
  {
    label: "Services",
    to: `${R}/services.html`,
    children: [
      { label: "Services", to: `${R}/services.html` },
      { label: "Detail service", to: `${R}/detail-service.html` },
      { label: "Pricing Plan", to: `${R}/pricing-plan.html` },
      { label: "Appointment", to: `${R}/appointment.html` },
    ],
  },
  { label: "Contact Us", to: `${R}/contact-us.html` },
  {
    label: "Pages",
    to: "#",
    children: [
      { label: "FAQ", to: `${R}/faq.html` },
      { label: "Blog Archive", to: `${R}/blog.html` },
      { label: "Single Post", to: `${R}/blog/the-moggies-and-doggies-philosophy-compassion-at-the-heart-of-every-visit.html` },
      { label: "Error 404", to: `${R}/404.html` },
    ],
  },
];

const SiteHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-pawelle-bg/95 backdrop-blur shadow-[0_6px_24px_rgba(67,48,109,0.08)] py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-[1290px] items-center justify-between px-5">
        <Link to={`${R}/home.html`} className="flex items-center">
          <img src="/pawelle/mnd-logo-h.webp" alt="Moggies & Doggies Veterinary Care – Affordable care that's always there" className="h-16 w-auto md:h-[76px]" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-8 lg:flex">
          {NAV.map((item) => (
            <div key={item.label} className="group relative">
              <Link
                to={item.to}
                className="flex items-center gap-1 font-body text-[16px] font-semibold text-pawelle-ink transition-colors hover:text-pawelle-primary"
              >
                {item.label}
                {item.children && <ChevronDown className="h-4 w-4 opacity-70" />}
              </Link>
              {item.children && (
                <div className="invisible absolute left-1/2 top-full z-50 mt-3 w-56 -translate-x-1/2 translate-y-2 rounded-2xl border border-pawelle-primary/10 bg-white p-2 opacity-0 shadow-[0_20px_50px_rgba(67,48,109,0.16)] transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                  {item.children.map((c) => (
                    <Link
                      key={c.label}
                      to={c.to}
                      className="block rounded-xl px-4 py-2.5 font-body text-[15px] font-semibold text-pawelle-ink transition-colors hover:bg-pawelle-bg hover:text-pawelle-primary"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to={`${R}/appointment.html`}
            className="inline-flex items-center gap-2 rounded-full bg-pawelle-primary px-6 py-3 font-display text-[15px] font-medium text-white shadow-[0_10px_24px_rgba(114,82,155,0.35)] transition-all duration-300 hover:bg-pawelle-primary-dark hover:-translate-y-0.5"
          >
            <CalendarDays className="h-4 w-4" />
            Book an Appointment
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-pawelle-primary text-white lg:hidden"
          aria-label="Menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden">
          <nav className="mx-4 mt-3 rounded-2xl bg-white p-4 shadow-[0_20px_50px_rgba(67,48,109,0.16)]">
            {NAV.map((item) => (
              <div key={item.label} className="border-b border-pawelle-bg last:border-0">
                <div className="flex items-center justify-between">
                  <Link
                    to={item.to}
                    onClick={() => !item.children && setMobileOpen(false)}
                    className="block py-3 font-body font-bold text-pawelle-ink"
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <button
                      onClick={() => setOpenGroup(openGroup === item.label ? null : item.label)}
                      className="p-2 text-pawelle-primary"
                      aria-label="Expand"
                    >
                      <ChevronDown className={`h-4 w-4 transition-transform ${openGroup === item.label ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
                {item.children && openGroup === item.label && (
                  <div className="pb-2 pl-3">
                    {item.children.map((c) => (
                      <Link
                        key={c.label}
                        to={c.to}
                        onClick={() => setMobileOpen(false)}
                        className="block py-2 font-body text-[15px] font-semibold text-pawelle-body"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link
              to={`${R}/appointment.html`}
              onClick={() => setMobileOpen(false)}
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-pawelle-primary px-6 py-3 font-display text-[15px] text-white"
            >
              <CalendarDays className="h-4 w-4" />
              Book an Appointment
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default SiteHeader;
